import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Spinner, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaEdit, FaTrashAlt, FaPlus } from "react-icons/fa";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import {
  useAccounts,
  AccountSelect,
  NoAccountsWarning,
  financeAuthPayload,
} from "../../components/finance/AccountSelect";
import FinanceBreadcrumb from "../../components/finance/Breadcrumb";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Liabilities — top-level register / hub for all school liabilities.
 *
 * Creating a liability ACCRUES it: the backend posts a journal
 * (Dr Expense account, Cr Liability account), which is why the create form
 * collects both GL accounts. Payment happens on the Accounts Payable page via
 * payables/pay. Per the API contract, only descriptive fields (name, due date,
 * description) may be edited after accrual — the amount and its GL accounts are
 * locked once the journal is posted.
 */
const Liabilities = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const { accounts, loading: accountsLoading } = useAccounts();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", search: "" });

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  function blankForm() {
    return {
      liability_name: "",
      liability_category: "current",
      liability_account_id: "",
      expense_account_id: "",
      liability_amount: "",
      liability_date: new Date().toISOString().slice(0, 10),
      liability_due_date: "",
      liability_description: "",
    };
  }

  const fetchLiabilities = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        ...financeAuthPayload(),
        status: filters.status,
        search: filters.search,
      };
      const res = await simplePostCall(
        ApiConfig.liabilityList,
        JSON.stringify(payload)
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Liability list fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.search]);

  useEffect(() => {
    fetchLiabilities();
  }, [fetchLiabilities]);

  // Aggregate totals for the summary tiles.
  const totals = rows.reduce(
    (acc, r) => {
      const amt = parseFloat(r.liability_amount) || 0;
      const paid = parseFloat(r.liability_amount_paid) || 0;
      const balance = amt - paid;
      acc.total += amt;
      acc.paid += paid;
      acc.outstanding += balance;
      if (r.liability_category === "non_current") acc.long_term += balance;
      else acc.short_term += balance;
      return acc;
    },
    { total: 0, paid: 0, outstanding: 0, short_term: 0, long_term: 0 }
  );

  const openCreateModal = () => {
    setEditing(null);
    setForm(blankForm());
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditing(row);
    setForm({
      liability_name: row.liability_name || "",
      liability_category: row.liability_category || "current",
      liability_account_id: row.liability_account_id || "",
      expense_account_id: row.expense_account_id || "",
      liability_amount: row.liability_amount || "",
      liability_date: row.liability_date
        ? String(row.liability_date).slice(0, 10)
        : "",
      liability_due_date: row.liability_due_date
        ? String(row.liability_due_date).slice(0, 10)
        : "",
      liability_description: row.liability_description || "",
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    // Descriptive-only update after accrual.
    if (editing) {
      if (!form.liability_name) {
        toast.warning("Name is required.");
        return;
      }
      await submit(ApiConfig.liabilityUpdate, {
        ...financeAuthPayload(),
        liability_id: editing.liability_id,
        liability_name: form.liability_name,
        liability_due_date: form.liability_due_date,
        liability_description: form.liability_description,
      });
      return;
    }

    // Create = accrual, needs both GL accounts.
    if (
      !form.liability_name ||
      !form.liability_amount ||
      !form.liability_account_id ||
      !form.expense_account_id ||
      !form.liability_date
    ) {
      toast.warning(
        "Name, amount, accrual date, liability account and expense account are all required."
      );
      return;
    }
    await submit(ApiConfig.liabilityCreate, {
      ...financeAuthPayload(),
      liability_name: form.liability_name,
      liability_category: form.liability_category,
      liability_account_id: Number(form.liability_account_id),
      expense_account_id: Number(form.expense_account_id),
      liability_amount: Number(form.liability_amount),
      liability_date: form.liability_date,
      liability_due_date: form.liability_due_date,
      liability_description: form.liability_description,
    });
  };

  const submit = async (url, payload) => {
    setSaving(true);
    try {
      const res = await simplePostCall(url, JSON.stringify(payload));
      if (res?.result) {
        toast.success(editing ? "Liability updated." : "Liability accrued.");
        setShowModal(false);
        fetchLiabilities();
      } else {
        toast.error(res?.message || "Save failed.");
      }
    } catch (err) {
      console.error("Save liability error:", err);
      toast.error("An error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete liability "${row.liability_name}"? This only works if no payment has been posted against it.`
      )
    )
      return;
    try {
      const res = await simplePostCall(
        ApiConfig.liabilityDelete,
        JSON.stringify({
          ...financeAuthPayload(),
          liability_id: row.liability_id,
        })
      );
      if (res?.result) {
        toast.success("Liability deleted.");
        fetchLiabilities();
      } else {
        toast.error(res?.message || "Delete failed — it may already be posted.");
      }
    } catch (err) {
      toast.error("An error occurred during delete.");
    }
  };

  const statusPill = (status) => {
    const s = String(status || "open").toLowerCase();
    if (s === "paid") return "lib-status lib-status-paid";
    if (s === "partial_paid" || s === "partial" || s === "partially_paid")
      return "lib-status lib-status-partial";
    if (s === "overdue") return "lib-status lib-status-overdue";
    return "lib-status lib-status-unpaid";
  };

  const statusLabel = (s) =>
    ({
      paid: "Paid",
      partial_paid: "Partially Paid",
      partial: "Partially Paid",
      partially_paid: "Partially Paid",
      overdue: "Overdue",
      open: "Open",
    }[String(s || "open").toLowerCase()] || "Open");

  const noAccounts = !accountsLoading && accounts.length === 0;

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <FinanceBreadcrumb
          items={[{ label: "Accounts Payable" }, { label: "Liabilities" }]}
        />
        <div className="special-header panel-white-all d-flex justify-content-between align-items-center">
          <h4 className="page-title">Liabilities</h4>
          <Button
            onClick={openCreateModal}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            <FaPlus className="me-1" /> Add Liability
          </Button>
        </div>

        {/* Summary tiles */}
        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-total">
            <span className="lib-tile-label">Total Liabilities</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.total)}
            </span>
          </div>
          <div className="lib-tile lib-tile-paid">
            <span className="lib-tile-label">Paid To Date</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.paid)}
            </span>
          </div>
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Outstanding</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
          <div className="lib-tile lib-tile-short">
            <span className="lib-tile-label">Current (Short-Term)</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.short_term)}
            </span>
          </div>
          <div className="lib-tile lib-tile-long">
            <span className="lib-tile-label">Non-Current (Long-Term)</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.long_term)}
            </span>
          </div>
        </div>

        {/* Quick links to specialised sub-pages */}
        <div className="lib-quicklinks">
          <Link to="/accountant/liabilities/payables" className="lib-quicklink">
            <span className="lib-quicklink-title">Accounts Payable</span>
            <span className="lib-quicklink-sub">Pay accrued liabilities</span>
          </Link>
          <Link to="/accountant/liabilities/loans" className="lib-quicklink">
            <span className="lib-quicklink-title">Loans</span>
            <span className="lib-quicklink-sub">Bank loans &amp; vehicle financing</span>
          </Link>
          <Link to="/accountant/liabilities/salaries" className="lib-quicklink">
            <span className="lib-quicklink-title">Salaries Payable</span>
            <span className="lib-quicklink-sub">Accrued unpaid staff salaries</span>
          </Link>
        </div>

        {/* Filters */}
        <div className="lib-filter-bar">
          <Form.Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="partial_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Search by name"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Button
            variant="secondary"
            onClick={() => setFilters({ status: "", search: "" })}
          >
            Clear
          </Button>
        </div>

        {/* Table */}
        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name / Description</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Due Date</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">
                    No liabilities recorded.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const amt = parseFloat(r.liability_amount) || 0;
                  const paid = parseFloat(r.liability_amount_paid) || 0;
                  const bal = amt - paid;
                  const posted = paid > 0;
                  return (
                    <tr key={r.liability_id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="fw-semibold">{r.liability_name}</div>
                        {r.liability_description && (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {r.liability_description}
                          </div>
                        )}
                      </td>
                      <td>
                        {r.liability_category === "non_current"
                          ? "Long-Term"
                          : "Short-Term"}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(amt)}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(paid)}
                      </td>
                      <td className="fw-bold">
                        {customer_currency} {formatNumber(bal)}
                      </td>
                      <td>
                        {r.liability_due_date
                          ? new Date(r.liability_due_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <span className={statusPill(r.liability_status)}>
                          {statusLabel(r.liability_status)}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm"
                          title="Edit"
                          onClick={() => openEditModal(r)}
                        >
                          <FaEdit />
                        </button>
                        {!posted && (
                          <button
                            className="btn btn-sm"
                            title="Delete"
                            onClick={() => handleDelete(r)}
                          >
                            <FaTrashAlt color="red" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* Create / Edit modal */}
      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? "Edit Liability" : "Accrue Liability"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editing && noAccounts && <NoAccountsWarning />}
          {editing && (
            <div className="fin-inline-warning">
              This liability is already accrued. Only the name, due date and
              description can be changed — the amount and its ledger accounts are
              locked.
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-8">
              <Form.Group>
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="liability_name"
                  value={form.liability_name}
                  onChange={handleFormChange}
                  placeholder="e.g. Office supplies invoice #INV-9821"
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Type</Form.Label>
                <Form.Select
                  name="liability_category"
                  value={form.liability_category}
                  onChange={handleFormChange}
                  disabled={!!editing}
                >
                  <option value="current">Current (Short-Term)</option>
                  <option value="non_current">Non-Current (Long-Term)</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Liability Account (credit){" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <AccountSelect
                  accounts={accounts}
                  name="liability_account_id"
                  types={["liability"]}
                  value={form.liability_account_id}
                  onChange={handleFormChange}
                  disabled={!!editing}
                  placeholder="Select liability account"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Expense Account (debit){" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <AccountSelect
                  accounts={accounts}
                  name="expense_account_id"
                  types={["expense"]}
                  value={form.expense_account_id}
                  onChange={handleFormChange}
                  disabled={!!editing}
                  placeholder="Select expense account"
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>
                  Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="liability_amount"
                  value={form.liability_amount}
                  onChange={handleFormChange}
                  disabled={!!editing}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>
                  Accrual Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="liability_date"
                  value={form.liability_date}
                  onChange={handleFormChange}
                  disabled={!!editing}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  name="liability_due_date"
                  value={form.liability_due_date}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>

            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Description / Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="liability_description"
                  value={form.liability_description}
                  onChange={handleFormChange}
                  placeholder="Supplier name, accrual context, etc."
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || (!editing && noAccounts)}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {saving ? (
              <Spinner size="sm" />
            ) : editing ? (
              "Save Changes"
            ) : (
              "Accrue Liability"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Liabilities;
